interface DrawerSwipeBackOptions {
  swipeThreshold?: number
}

export function useDrawerSwipeBack<T extends string>(containerRef: Ref<HTMLElement | undefined | null>, view: Ref<T>, defaultView: T, options: DrawerSwipeBackOptions = {}) {
  const { swipeThreshold = 80 } = options
  const slideDirection = ref<'left' | 'right'>('left')

  let activePointerId: number | null = null
  let startX = 0
  let startY = 0
  let isSwiping = false
  let directionDecided = false

  function onPointerDown(e: PointerEvent) {
    if (view.value === defaultView) {
      return
    }

    activePointerId = e.pointerId
    startX = e.clientX
    startY = e.clientY
    isSwiping = false
    directionDecided = false
  }

  function onPointerMove(e: PointerEvent) {
    if (activePointerId === null || e.pointerId !== activePointerId) {
      return
    }

    if (view.value === defaultView) {
      return
    }

    if (directionDecided && !isSwiping) {
      return
    }

    const deltaX = e.clientX - startX
    const deltaY = e.clientY - startY

    if (!directionDecided && (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10)) {
      directionDecided = true
      isSwiping = Math.abs(deltaX) > Math.abs(deltaY)
    }
  }

  function onPointerUp(e: PointerEvent) {
    if (activePointerId === null || e.pointerId !== activePointerId) {
      return
    }
    activePointerId = null

    if (!isSwiping) {
      return
    }

    const deltaX = e.clientX - startX
    if (deltaX > swipeThreshold) {
      slideDirection.value = 'right'
      view.value = defaultView
    }
    isSwiping = false
    directionDecided = false
  }

  function onPointerCancel(e: PointerEvent) {
    if (activePointerId === null || e.pointerId !== activePointerId) {
      return
    }
    activePointerId = null
    isSwiping = false
    directionDecided = false
  }

  watch(containerRef, (el, _, onCleanup) => {
    if (!el || !import.meta.client) {
      return
    }

    el.addEventListener('pointerdown', onPointerDown)
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
    window.addEventListener('pointercancel', onPointerCancel)
    // Prevent intercepting horizontal swipe gestures with vertical scrolling
    el.style.touchAction = 'pan-y'

    onCleanup(() => {
      el.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
      window.removeEventListener('pointercancel', onPointerCancel)
      el.style.touchAction = ''
    })
  }, { immediate: true })

  return {
    slideDirection,
  }
}
